import { useRef, useState, useEffect } from "react";
import apiService from "services/api";
import { useHistory } from "react-router-dom";
import { useNotificationContext } from "contexts";
import { useFieldArray, useForm } from "react-hook-form";
import { ImageUpload } from "components";
import { FormGroup, Label, Row, Col, Button } from "reactstrap";
import { locales } from "utils/locales";
import { Link } from "react-router-dom";
import useProgressBar from "components/ProgressBar/useProgressBar";
import { columns, initialState } from "./constants";
import NestedFieldOptions from "./NestedOptions";

const NewMenuItemPage = () => {
	const history = useHistory();
	const [showLoading, setShowLoading] = useState(false);
	const [ProgressBar, startProgress, finishProgress] = useProgressBar();
	const fileInputRef = useRef();

	const {
		register,
		handleSubmit,
		control,
		watch,
		formState: { errors },
	} = useForm({
		defaultValues: initialState,
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "options",
	});

	const { createNotification } = useNotificationContext();

	const removeOption = (idx) => () => remove(idx);

	const submitNewMenuItemForm = async (data) => {
		var file;
		if (fileInputRef.current?.files?.length) {
			setShowLoading(true);
			startProgress();
			file = fileInputRef.current.files[0];
		}

		try {
			const generator = apiService.createMenuItem(data, file);
			const imageUploadYield = await generator.next();
			if (imageUploadYield.value) finishProgress();
			await generator.next();
			createNotification({
				type: "success",
				message: "Successfully added item in menu",
			});
			return history.push("/coffee/menu-item");
		} catch (error) {
			return createNotification({
				type: "danger",
				message: error.message,
			});
		} finally {
			setShowLoading(false);
			finishProgress();
		}
	};

	return (
		<form onSubmit={handleSubmit(submitNewMenuItemForm)} className="content">
			<div className="d-flex justify-content-center">
				<div className="w-75">
					<div className="my-3">
						<Link to="/coffee/menu-item" className="btn btn-sm btn-info my-3">
							&lt; Back to menu
						</Link>
					</div>
					<h5 className="text-center mb-4">New Menu Item</h5>

					<div className="mb-3">
						<FormGroup className="mb-1">
							<Label for="Title" className="text-white">
								Title
							</Label>
						</FormGroup>
						{locales.map((locale, key) => (
							<div className="input-group mb-2" key={key}>
								<div className="input-group-prepend">
									<span className="input-group-text pr-2">{locale.tag}</span>
								</div>
								<input
									type="text"
									className="form-control"
									{...register(`title${locale.languageShort}`, {
										required: locale.tag === "EN" ? true : undefined,
									})}
									placeholder={`Title in ${locale.languageLong}`}
								/>
							</div>
						))}
					</div>
					<div className="mb-3">
						<FormGroup className="mb-1">
							<Label for="Description" className="text-white">
								Description
							</Label>
						</FormGroup>
						{locales.map((locale, key) => (
							<div className="input-group mb-2" key={key}>
								<div className="input-group-prepend">
									<span className="input-group-text pr-2">{locale.tag}</span>
								</div>
								<input
									type="text"
									className="form-control"
									{...register(`description${locale.languageShort}`, {
										required: locale.tag === "EN" ? true : undefined,
									})}
									placeholder={`Description in ${locale.languageLong}`}
								/>
							</div>
						))}
					</div>

					<Row>
						<Col sm={6}>
							<FormGroup className="mb-4">
								<Label for="exampleEmail" className="text-white">
									Price
								</Label>
								<input
									className="form-control"
									type="number"
									{...register("price", {
										required: true,
										min: 0,
									})}
									placeholder="Reward Points"
								/>
							</FormGroup>
							<FormGroup className="mb-4">
								<Label for="exampleEmail" className="text-white">
									Category
								</Label>
								<select
									className="form-control"
									{...register("category", {
										required: true,
									})}
									placeholder="Category"
								>
									<option value="Drinks">Drinks</option>
									<option value="Food">Food</option>
									<option value="Others">Others</option>
								</select>
							</FormGroup>
							<FormGroup className="mb-4">
								<Label for="exampleEmail" className="text-white">
									Sub Category
								</Label>
								<select
									className="form-control"
									{...register("subCategory", {
										required: true,
									})}
									placeholder="Sub Category"
								>
									<option value="Beverage">Beverage</option>
									<option value="Coffee">Coffee</option>
									<option value="Coffee Beans">Coffee Beans</option>
									<option value="Mains">Mains</option>
									<option value="Others">Others</option>
								</select>
							</FormGroup>
						</Col>
						<Col className="text-center" sm={6}>
							<FormGroup>
								<Label for="exampleEmail" className="text-white">
									Image
								</Label>
							</FormGroup>
							<ImageUpload hasButton={false} fileInputRef={fileInputRef} />
							{showLoading && <ProgressBar />}
						</Col>
						<Col sm={6}>
							<FormGroup check className="mb-4">
								<Label check>
									<input type="checkbox" className="mr-2" {...register("isStaffOnly")} />
									<span className="form-check-sign">
										<span className="check"></span>
									</span>
									<span className="text-white">Staff Only?</span>
								</Label>
							</FormGroup>
						</Col>
						<Col sm={6}>
							<FormGroup check className="mb-4">
								<Label check>
									<input type="checkbox" className="mr-2" {...register("isFeatured")} />
									<span className="form-check-sign">
										<span className="check"></span>
									</span>
									<span className="text-white">Featured?</span>
								</Label>
							</FormGroup>
						</Col>
						<Col sm={6}>
							<FormGroup check className="mb-4">
								<Label check>
									<input type="checkbox" className="mr-2" {...register("isLatest")} />
									<span className="form-check-sign">
										<span className="check"></span>
									</span>
									<span className="text-white">Latest?</span>
								</Label>
							</FormGroup>
						</Col>
					</Row>
					<FormGroup className="mb-2">
						<Label className="text-white">Options</Label>
						{fields.length ? (
							fields.map((field, i) => (
								<NestedFieldOptions
									control={control}
									register={register}
									index={i}
									id={field.id}
									watch={watch}
									key={field.id}
									namePrefix={`options.[${i}]`}
									removeOption={removeOption(i)}
									errors={errors}
								/>
							))
						) : (
							<div>No Options</div>
						)}
						<Button size="sm" color="warning" className="mt-2" type="button" onClick={() => append(1)}>
							Add Option
						</Button>
					</FormGroup>
					<div className="d-flex justify-content-end mt-5">
						{showLoading ? (
							<Button color="primary" disabled={true}>
								<span className="spinner-border"></span>
							</Button>
						) : (
							<Button color="primary" type="submit">
								Submit
							</Button>
						)}
						<button
							className="btn btn-secondary"
							type="button"
							// onClick={resetFields}
						>
							Reset
						</button>
					</div>
				</div>
			</div>
		</form>
	);
};

export default NewMenuItemPage;
